import { useEffect, useRef, useState } from "react";
import { useDraftPrompt } from "../context/DraftPromptContext.jsx";

const HOURS = [
  ["00","12 AM"],["01","01 AM"],["02","02 AM"],["03","03 AM"],["04","04 AM"],
  ["05","05 AM"],["06","06 AM"],["07","07 AM"],["08","08 AM"],["09","09 AM"],
  ["10","10 AM"],["11","11 AM"],["12","12 PM"],["13","01 PM"],["14","02 PM"],
  ["15","03 PM"],["16","04 PM"],["17","05 PM"],["18","06 PM"],["19","07 PM"],
  ["20","08 PM"],["21","09 PM"],["22","10 PM"],["23","11 PM"],
];

const MINUTES = [
  ["00","00"],["10","10"],["20","20"],["30","30"],["40","40"],["50","50"],
];

function padTwo(n) { return String(n).padStart(2, "0"); }

function toMMDDYYYY(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${m}/${d}/${y}`;
}

function toYYYYMMDD(mdy) {
  if (!mdy) return "";
  const [m, d, y] = mdy.split("/");
  return `${y}-${padTwo(m)}-${padTwo(d)}`;
}

export default function DraftPromptSchedule() {
  const { state, patch } = useDraftPrompt();
  const [dpOpen, setDpOpen] = useState(false);
  const dpRef = useRef(null);
  const dpInitRef = useRef(false);

  // ── Inject CSS to keep datepicker above modal ─────────────────────────────
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "datepicker-zindex-fix";
    style.textContent = ".datepicker { z-index: 1065 !important; }";
    if (!document.getElementById("datepicker-zindex-fix")) {
      document.head.appendChild(style);
    }
    return () => document.getElementById("datepicker-zindex-fix")?.remove();
  }, []);

  // ── Init jQuery datepicker once — div is always in DOM so this only runs once
  useEffect(() => {
    if (dpInitRef.current || !window.$ || !dpRef.current) return;
    dpInitRef.current = true;

    const $dp = window.$(dpRef.current);

    const now = new Date();
    const start = `${padTwo(now.getMonth()+1)}/${padTwo(now.getDate())}/${now.getFullYear()}`;
    const endDate = new Date(now);
    endDate.setDate(now.getDate() + 90);
    const end = `${padTwo(endDate.getMonth()+1)}/${padTwo(endDate.getDate())}/${endDate.getFullYear()}`;

    $dp.datepicker({ startDate: start, endDate: end });

    if (state.userdate) {
      $dp.datepicker("setDate", toMMDDYYYY(state.userdate));
    }

    $dp.on("changeDate", function () {
      const formatted = $dp.datepicker("getFormattedDate");
      patch({ userdate: toYYYYMMDD(formatted) });
      setDpOpen(false);
    });
  }, []); // eslint-disable-line

  // Update datepicker when state.userdate changes externally
  useEffect(() => {
    if (!dpInitRef.current || !window.$ || !dpRef.current || !state.userdate) return;
    const $dp = window.$(dpRef.current);
    const current = $dp.datepicker("getFormattedDate");
    const want = toMMDDYYYY(state.userdate);
    if (current !== want) $dp.datepicker("setDate", want);
  }, [state.userdate]);

  const ampm = Number(state.userhour) >= 12 ? "PM" : "AM";
  const buttonLabel = state.userdate
    ? `${state.userdate}   ${state.userhour}:${state.userminute} ${ampm}`
    : "Date and Time";

  const handleScheduleToggle = (e) => {
    patch({ scheduled: e.target.checked });
  };

  return (
    <div className="row g-2 mt-4 mb-3">
      <div className="col">
        <label className="border px-3 py-2 rounded-4 d-block fw-bold">
          <div className="row g-0 align-items-center justify-content-between my-1">
            <div className="col-4">
              <i className="fi fi-rr-calendar text-primary me-2 fs-5" /> Schedule
            </div>

            <div className="col" id="section-select-datetime" style={{ display: state.scheduled ? "" : "none" }}>
                <div className="position-relative">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    type="button"
                    onClick={() => setDpOpen((v) => !v)}
                    style={{ fontSize: 12, padding: "5px 10px" }}
                  >
                    {buttonLabel}
                  </button>

                  {/* Inline panel — no Bootstrap dropdown so clicks don't get swallowed */}
                  <div
                    style={{
                      display: dpOpen ? "block" : "none",
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      zIndex: 1065,
                      background: "#fff",
                      border: "1px solid #dee2e6",
                      borderRadius: 8,
                      padding: 12,
                      width: 280,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    }}
                  >
                    {/* jQuery datepicker target — always in DOM */}
                    <div ref={dpRef} />

                    <div className="userdate mt-3">
                      <div className="row g-2">
                        <div className="col-6">
                          <select
                            className="form-select form-select-sm"
                            value={state.userhour}
                            onChange={(e) => patch({ userhour: e.target.value })}
                          >
                            {HOURS.map(([val, label]) => (
                              <option key={val} value={val}>{label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <select
                            className="form-select form-select-sm"
                            value={state.userminute}
                            onChange={(e) => patch({ userminute: e.target.value })}
                          >
                            {MINUTES.map(([val, label]) => (
                              <option key={val} value={val}>{label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            <div className="col-3 text-start text-sm-end">
              <div className="form-check form-switch fs-4 d-inline-block float-end">
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  role="switch"
                  id="post-creator-scheduled"
                  checked={state.scheduled}
                  onChange={handleScheduleToggle}
                />
              </div>
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}